/**
 * Centralised product display-name builder.
 * Concatenates name + mass + unit + size + model, filtering out empty segments
 * so there are never double-spaces or trailing whitespace.
 *
 * For `unit` price-type products mass/unit are omitted from the display string
 * (they show separately as "per kg" etc.).
 */

export type ProductLike = {
  bp_product_name?: string;
  bp_product_mass?: string | number;
  bp_product_unit_of_mass?: string;
  bp_product_size?: string;
  bp_product_model?: string;
  bp_product_price_type?: string;
  // Also support cart-item shape
  itemname?: string;
  mass?: string | number;
  unitofmass?: string;
  size?: string;
  model?: string;
  price_type?: string;
  // Picker / supplier shapes use different known keys (name, unitOfMass, …).
  // Index signature prevents TS weak-type "no properties in common" errors.
  [key: string]: any;
};

const joinLabel = (...parts: Array<string | null | undefined>): string =>
  parts
    .map(p => String(p || '').trim())
    .filter(Boolean)
    .join(' - ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Join parent product name + variant label without doubling.
 * `bp_variant_display_name` is sometimes attrs-only ("Red / L") and sometimes
 * already a full sellable label ("Shirt - Red / L").
 */
export const composeProductVariantLabel = (
  parentName: string | null | undefined,
  variantLabel: string | null | undefined,
): string => {
  const parent = String(parentName || '')
    .replace(/\s+/g, ' ')
    .trim();
  const variant = String(variantLabel || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!variant) return parent;
  if (!parent) return variant;

  const parentLower = parent.toLowerCase();
  const variantLower = variant.toLowerCase();
  if (variantLower === parentLower) return variant;
  // Already "Parent - …" / "Parent- …"
  if (
    variantLower.startsWith(`${parentLower} - `) ||
    variantLower.startsWith(`${parentLower}-`)
  ) {
    return variant;
  }
  return joinLabel(parent, variant);
};

/** Attrs-only / stored display name for a variant (no parent join). */
export const getVariantAttrLabel = (
  variant: Record<string, any> | null | undefined,
): string => {
  if (!variant || typeof variant !== 'object') return '';
  const explicit = String(variant.bp_variant_display_name || '').trim();
  if (explicit) return explicit;
  const attrs = variant.bp_variant_attributes;
  if (attrs && typeof attrs === 'object') {
    const joined = Object.values(attrs)
      .map(v => String(v || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (joined) return joined;
  }
  return '';
};

export const getProductDisplayName = (
  // Accept any product-shaped object. Call sites use incompatible weak types
  // (bp_* vs name/unitOfMass picker shapes); narrowing happens below.
  product: ProductLike | Record<string, any> | null | undefined,
): string => {
  if (!product || typeof product !== 'object') return '';

  const p = product as ProductLike;

  const priceType = String(
    p.bp_product_price_type || p.price_type || '',
  ).toLowerCase();

  const name = String(p.bp_product_name || p.itemname || '').trim();
  const massRaw = p.bp_product_mass ?? p.mass ?? '';
  const massStr = String(massRaw).trim();
  const massNum = Number(massStr);
  const hasMass = massStr.length > 0 && Number.isFinite(massNum) && massNum > 0;
  const unit = String(
    p.bp_product_unit_of_mass || p.unitofmass || '',
  ).trim();
  const size = String(p.bp_product_size ?? p.size ?? '').trim();
  const model = String(p.bp_product_model ?? p.model ?? '').trim();

  if (priceType === 'unit') {
    return [name, size, model].filter(Boolean).join(' ');
  }

  const massUnit = hasMass ? `${massStr}${unit}` : '';
  // Avoid "Red label 750Ml 750Ml" when API already baked mass into the name.
  const nameAlreadyHasMass =
    Boolean(massUnit) &&
    name.toLowerCase().includes(massUnit.toLowerCase());
  return [name, nameAlreadyHasMass ? '' : massUnit, size, model]
    .filter(Boolean)
    .join(' ');
};

/**
 * Sales / report / PO rows shaped like
 * `{ productInformation, variantInformation? }` or a flat product doc.
 */
export const getProductRowDisplayName = (
  row: Record<string, any> | null | undefined,
): string => {
  if (!row || typeof row !== 'object') return '';

  const product =
    row.productInformation ||
    row.product ||
    row.bp_product_information ||
    null;
  const variant =
    row.variantInformation ||
    row.bp_variant_information ||
    row.variant ||
    null;

  const variantName = getVariantAttrLabel(variant);
  if (variantName) {
    const parent = String(
      (product && product.bp_product_name) || row.bp_product_name || '',
    ).trim();
    return composeProductVariantLabel(parent, variantName);
  }

  const snapshotName = String(
    row.bp_product_name_snapshot ||
      row.snapshotProductName ||
      row.product_name_snapshot ||
      '',
  ).trim();
  if (snapshotName) return snapshotName;

  if (product) return getProductDisplayName(product);

  // Flat report/API docs
  return getProductDisplayName({
    ...row,
    bp_product_name: row.bp_product_name || row.productName,
  });
};

/**
 * Order-line display. Variant lines already carry the sellable label in
 * `bp_product_name` from the backend; non-variants append mass/size/model.
 */
export const getOrderLineProductDisplayName = (
  item: Record<string, any> | null | undefined,
): string => {
  if (!item || typeof item !== 'object') return '';
  const info = item.bp_product_information || item;
  const variantId = info?.bp_variant_id ?? item?.bp_variant_id;
  // Only a concrete variant id marks a variant line — not has_variants on a snapshot.
  if (variantId) {
    const lineName = String(info?.bp_product_name || '').trim();
    if (lineName) return lineName;
    const parent = String(info?.bp_product_parent_name || '').trim();
    if (parent) return parent;
  }
  return getProductDisplayName(info);
};
